import React, {FC} from 'react';

import Panel from '@/components/Panels/common/Panel';

import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';

import PlaceIcon from '@mui/icons-material/Place';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import AreaIcon from '@mui/icons-material/ZoomInMap';

import SubTitle from '@/components/forms/SubTitle';
import InfoBox from '@/components/Panels/common/InfoBox';

import {DISPUTE_STATUS_CHIP_CONFIGURATION, PANEL_WIDTH, PROJECT_STAGE_CHIP_CONFIGURATION} from '@/config';
import {useTranslation} from 'react-i18next';

import {ParcelSummary} from '@/domain/entities/ParcelSummary';
import {Dispute} from '@/domain/entities/Dispute';
import {SelectedParcelMode} from '@/components/Panels/Parcel/index';
import {DocumentProperties} from '@/domain/entities/ParcelDocument';
import {User} from '@/domain/entities/User';
import AddButton from '@/components/AddButton';
import {Project} from '@/domain/entities/Project';

//TYPES
export type ParcelPanelProps = {
  project?: Project,
  user?: User,
  parcelSummary?: ParcelSummary,
  dispute?: Dispute,
  documents?: Array<DocumentProperties>,
  onDisputePanelOpen: () => void
  onDocumentListPanelOpen: () => void,
  onSelectedParcelModeChanged: (selectedParcelMode: SelectedParcelMode) => void
};

const InfoPanel: FC<ParcelPanelProps> = ({project, user, parcelSummary, dispute, documents, onSelectedParcelModeChanged, onDocumentListPanelOpen}) => {

  const {t} = useTranslation();

  if(!parcelSummary) return; 

  const isOwner = user?.role === 'USER';

  const {area, polygon, parcel, subparcel, city, ownerName, ownerEmail, projectZone} = parcelSummary;

  const handleDisputeOpen = () => onSelectedParcelModeChanged(SelectedParcelMode.DISPUTE);
  const handleDocumentsOpen = () => onSelectedParcelModeChanged(SelectedParcelMode.DOCUMENTS);

  const disputeStatusConfig = dispute?.status
    ? DISPUTE_STATUS_CHIP_CONFIGURATION[dispute.status]
    : ['#ccc', '#000'];

  const projectStageConfig = project && project.stage && {
    backgroundColor: PROJECT_STAGE_CHIP_CONFIGURATION[project.stage][0],
    color: PROJECT_STAGE_CHIP_CONFIGURATION[project.stage][1],
    variant: PROJECT_STAGE_CHIP_CONFIGURATION[project.stage][2]
  };

  const projectStage = project && project.stage
    ?  <Chip size="small" label={t(`stage.${project?.stage}`).toUpperCase()}
      variant={projectStageConfig?.variant}
      sx={{backgroundColor: projectStageConfig?.backgroundColor, color: projectStageConfig?.color, minWidth: '120px', fontWeight: 700, mt: 1}}
    />
    : <Typography variant="body1" sx={{pl: 3.5}}>-</Typography>;


  const chipDispute = {
    maxWidth: '150px',
    border: '1px solid white',
    color: disputeStatusConfig[1],
    bgcolor: disputeStatusConfig[0],
    fontWeight: 700,
    '& .MuiChip-icon': {
      color: 'white'
    },
    '&:hover': {
      bgcolor: 'transparent',
      border: `1px solid ${disputeStatusConfig[0]}`,
      color: disputeStatusConfig[0],
      fontWeight: 700,
      '& .MuiChip-icon': {
        color: disputeStatusConfig[0]
      }
    }
  };
  
  return <Panel width={PANEL_WIDTH.parcel}>
    <Stack direction="column">
      <Stack direction="row" sx={{alignItems: 'center', gap: 1, mb: 2}}>
        <PlaceIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
        <Typography variant="h6" sx={{color: 'primary.main', m: 0, p: 0}}>{t('parcelPanel.title')}</Typography>
      </Stack>
      <Stack direction="row" sx={{justifyContent: 'space-between', gap: 1}}>
        <InfoBox label={t('parcelPanel.polygon')} value={polygon}/>
        <InfoBox label={t('parcelPanel.parcel')} value={parcel}/>
        <InfoBox label={t('parcelPanel.subparcel')} value={subparcel}/>
      </Stack>
      <Divider sx={{my: 2}}/>
      <Stack direction="column" sx={{gap: 2}}>
        <Stack direction="row" sx={{justifyContent: 'space-between', gap: 2, mb: 2}}>
          <SubTitle label={`${t('parcelPanel.project')}-${t('parcelPanel.stage')}`} icon={<FileCopyIcon sx={styles.infoIcon}/>}>
            <Stack direction="row" sx={{justifyContent: 'space-between', gap: 2, alignItems: 'center'}}>
              <Typography variant="body1" sx={{pl: 3.5}}>{projectZone}</Typography>
              {projectStage}
            </Stack>
          </SubTitle>
        </Stack>
        <Stack direction="row" sx={{justifyContent: 'space-between', gap: 2}}>
          <SubTitle label={t('parcelPanel.city')} icon={<LocationCityIcon sx={styles.infoIcon}/>}>
            <Typography variant="body1" sx={{pl: 3.5}}>{city}</Typography>
          </SubTitle>
          <SubTitle label={t('parcelPanel.area')} icon={<AreaIcon sx={styles.infoIcon}/>}>
            <Typography variant="body1" sx={{pl: 3.5}}>{`${Math.trunc(area)} m`}<sup>2</sup></Typography>
          </SubTitle>
        </Stack>
      </Stack>

      <Divider sx={{my: 2}}/>

      <SubTitle label={t('parcelPanel.owner')} icon={<PersonIcon sx={styles.infoIcon}/>}>
        <Typography variant="body1" sx={{pl: 3.5}}>{ownerName}</Typography>
        <Typography variant="body1" sx={{pl: 3.5, color: 'grey.500'}}>{ownerEmail}</Typography>
      </SubTitle>

      <Divider sx={{my: 2}}/>

      <Stack direction="row" sx={{pl: 0, gap: 1, alignItems: 'center', justifyContent: 'space-between'}}>
        <SubTitle label={t('parcelPanel.dispute')} icon={<AnnouncementIcon sx={styles.infoIcon}/>}/>
        {
          dispute?.status
            ? <Chip label={t(isOwner ? 'disputePanel.pending' : `disputePanel.${dispute?.status.toLowerCase()}`)} onClick={handleDisputeOpen} icon={<VisibilityIcon/>} sx={chipDispute}/>
            : <AddButton onClick={handleDisputeOpen} disabled={false}/>
        }
      </Stack>

      <Divider sx={{my: 2}}/>

      <Stack direction="row" sx={{pl: 0, gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1}}>
        <SubTitle label={t('parcelPanel.documents')} icon={<DescriptionIcon sx={styles.infoIcon}/>}/>
        {
          documents && documents?.length > 0
            ? <Chip label={`${documents.length} ${documents.length === 1 ? t('parcelPanel.document') : t('parcelPanel.documents')}`} icon={<VisibilityIcon/>} onClick={onDocumentListPanelOpen} sx={styles.chipDocument}/>
            : <AddButton onClick={handleDocumentsOpen} disabled={false}/>
        }
      </Stack>
    </Stack>
  </Panel>;
};
export default InfoPanel;

const styles = {
  infoIcon: {
    fontSize: '20px',
    color: 'primary.main',
  },
  subtitle: {
    color: 'text.secondary',
    m: 0,
    pb: 0.25,
    lineHeight: '16px',
    minWidth: '70px'
  },
  chipDocument: {
    maxWidth: '150px',
    border: '1px solid white',
    fontWeight: 700,
    '&:hover': {
      bgcolor: 'transparent',
      border: '1px solid #616161',
      fontWeight: 700,
    }}
};